// The patient-balances worklist and campaign endpoints, in one place.
//
// W1a owns `GET /balances` and the CSV export, W1b the campaign routes (both in
// backend/app/routes/outreach.py); the contract is docs/outreach.md "Patient
// balances" / "Campaigns". Every path is practice-scoped, every amount is integer
// CENTS, and every field name this page reads is named in this file and nowhere
// else. CSRF is attached by the wrapper the app installs on window.fetch, so a plain
// fetch is correct.
//
// Nothing in this module sends anything on its own. `startCampaign` is called from
// one place — the Start button of the campaign modal — and only after a preview of
// the same channel, template and recipients has been shown; it posts `confirm: true`,
// which the server requires. A campaign is a background job on the server: this file
// only reads its progress.

import {
  centsToDollars,
  dollarsToCents,
  formatDay,
  formatWhen,
  sendStatusChip,
} from "../collections/api";
import {
  channelLabel,
  fetchStatus,
  fetchTemplates,
  formatCents,
} from "../outreach/api";

export {
  centsToDollars,
  channelLabel,
  dollarsToCents,
  fetchStatus,
  fetchTemplates,
  formatCents,
  formatDay,
  formatWhen,
  sendStatusChip,
};

const API_URL = process.env.REACT_APP_API_URL || "";

// Jordan's default filter: balance ≥ $25, oldest DOS more than 60 days ago. A last
// outreach of 0 days means "no filter" on the wire. Opted-out patients are excluded
// unless asked for.
export const DEFAULT_FILTERS = {
  minBalanceCents: 2500,
  oldestDosBeforeDays: 60,
  lastOutreachBeforeDays: 0,
  hasMobile: false,
  hasEmail: false,
  includeOptedOut: false,
};

// The server caps `limit` at 2000 and says `truncated` when the list was cut.
export const LIST_LIMIT = 500;

// The rate the server accepts (per campaign, messages per second).
export const RATE_MIN = 0.2;
export const RATE_MAX = 2.0;
export const RATE_DEFAULT = 1.0;

// How often the progress panel re-reads a running campaign.
export const POLL_MS = 2500;

// The channels a campaign may use. Calls are not campaigns.
export const CHANNELS = [
  { key: "sms", label: "SMS" },
  { key: "email", label: "Email" },
];

// The ONE builder for the list's query string. The export button calls it too, with
// `forExport`, so the file matches the screen (minus `limit`: the export's own cap is
// the server's). Only a checked channel box is sent; unchecked means "no filter", not
// "has none".
export function balancesQueryParams(filters, { forExport = false } = {}) {
  const f = { ...DEFAULT_FILTERS, ...(filters || {}) };
  const qs = new URLSearchParams();
  qs.set("min_balance_cents", String(f.minBalanceCents ?? 0));
  qs.set("oldest_dos_before_days", String(f.oldestDosBeforeDays ?? 0));
  qs.set("last_outreach_before_days", String(f.lastOutreachBeforeDays ?? 0));
  if (f.hasMobile === true) qs.set("has_mobile", "1");
  if (f.hasEmail === true) qs.set("has_email", "1");
  qs.set("include_opted_out", f.includeOptedOut === true ? "1" : "0");
  if (!forExport) qs.set("limit", String(LIST_LIMIT));
  return qs;
}

// "60" → 60; blank → null; anything that is not a whole non-negative number → NaN,
// which the caller refuses before the server has to.
export function parseDays(text) {
  const raw = String(text ?? "").trim();
  if (!raw) return null;
  if (!/^\d{1,4}$/.test(raw)) return NaN;
  return Number(raw);
}

// "1" → 1; "0.5" → 0.5; outside RATE_MIN..RATE_MAX or unreadable → NaN.
export function parseRate(text) {
  const raw = String(text ?? "").trim();
  if (!raw) return NaN;
  if (!/^\d+(\.\d+)?$/.test(raw)) return NaN;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < RATE_MIN || n > RATE_MAX) return NaN;
  return n;
}

// Never surface a server message we did not write. The routes return {error: "..."}
// for the refusals a biller can act on (400 filters, 409 cap / already running) and a
// generic sentence for anything else.
async function readError(response, fallback) {
  try {
    const body = await response.json();
    return { message: (body && body.error) || fallback, body: body || {} };
  } catch (err) {
    return { message: fallback, body: {} };
  }
}

async function request(url, options, fallback) {
  const response = await fetch(url, { credentials: "include", ...options });
  if (!response.ok) {
    const { message, body } = await readError(response, fallback);
    const error = new Error(message);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  // A 2xx is not a payload. A truncated body or a proxy's HTML page parses to null,
  // and a caller defaulting that to [] would render "nobody owes anything" at exactly
  // the moment we could not check.
  const body = await response.json().catch(() => null);
  if (!body || typeof body !== "object") {
    const error = new Error(
      "The response could not be read, so this may be incomplete. Try again.",
    );
    error.status = response.status;
    error.body = {};
    throw error;
  }
  return body;
}

function post(payload) {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload == null ? {} : payload),
  };
}

const base = (practiceId) => `${API_URL}/api/practices/${practiceId}/outreach`;
const campaignBase = (practiceId, campaignId) =>
  `${base(practiceId)}/campaigns/${encodeURIComponent(campaignId)}`;

const num = (value) => {
  const n = Number(value);
  return value == null || value === "" || !Number.isFinite(n) ? null : n;
};
const str = (value) => (typeof value === "string" ? value : null);

// ---- the worklist ----

// The promise is interpreted by the server (never cleared by code): kept when a
// payment landed after it was made, broken when the date passed without one.
const PROMISE_STATES = {
  kept: { label: "Kept", tone: "good" },
  broken: { label: "Broken", tone: "bad" },
  pending: { label: "Pending", tone: "info" },
};

export function promiseChip(state) {
  if (!state || state === "none") return null;
  return (
    PROMISE_STATES[state] || { label: String(state), tone: "muted" }
  );
}

export function normalizeRow(raw) {
  const r = raw || {};
  const statement = r.last_statement || null;
  const outreach = r.last_outreach || null;
  const promise = r.promise || null;
  return {
    patientId: r.patient_id,
    displayName: str(r.display_name),
    firstName: str(r.first_name),
    balanceCents: num(r.balance_cents) ?? 0,
    oldestDos: str(r.oldest_dos),
    openClaims: num(r.open_claims) ?? 0,
    lastStatement: statement
      ? { sentAt: str(statement.sent_at), status: str(statement.status) }
      : null,
    lastOutreach: outreach
      ? {
          at: str(outreach.at),
          channel: str(outreach.channel),
          kind: str(outreach.kind),
        }
      : null,
    promise: promise
      ? {
          by: str(promise.by),
          setAt: str(promise.set_at),
          state: str(promise.state) || "pending",
        }
      : null,
    paymentsSinceOutreachCents: num(r.payments_since_outreach_cents) ?? 0,
    paymentsSinceOutreachCount: num(r.payments_since_outreach_count) ?? 0,
    // Only an explicit true means a channel exists or may be used; a body we cannot
    // read never offers one.
    hasMobile: r.has_mobile === true,
    hasEmail: r.has_email === true,
    canText: r.can_text === true,
    canTextReason: str(r.can_text_reason),
    canEmail: r.can_email === true,
    canEmailReason: str(r.can_email_reason),
    optedOut: r.opted_out === true,
  };
}

// GET …/balances?<filters> → normalised. Refuses a body without a rows array rather
// than rendering an all-clear.
export async function fetchBalances(practiceId, filters) {
  const qs = balancesQueryParams(filters);
  const body = await request(
    `${base(practiceId)}/balances?${qs.toString()}`,
    {},
    "Could not load the patient balances.",
  );
  if (!Array.isArray(body.rows)) {
    throw new Error(
      "The response could not be read, so this list may be incomplete. Try again.",
    );
  }
  const totals = body.totals || {};
  return {
    rows: body.rows.map(normalizeRow),
    total: num(body.total) ?? body.rows.length,
    truncated: body.truncated === true,
    totals: {
      balanceCents: num(totals.balance_cents) ?? 0,
      patients: num(totals.patients) ?? body.rows.length,
    },
    today: str(body.today),
  };
}

// ---- campaigns ----

// outreach_campaigns.status → what the chip says and how it is coloured. `stalled`
// is the server's reading of a stale heartbeat, never this file's.
const CAMPAIGN_STATUS = {
  queued: { label: "Queued", tone: "muted" },
  running: { label: "Running", tone: "info" },
  done: { label: "Done", tone: "good" },
  cancelled: { label: "Cancelled", tone: "muted" },
  failed: { label: "Failed", tone: "bad" },
  stalled: { label: "Stalled", tone: "warn" },
};

export function campaignStatusChip(status) {
  return CAMPAIGN_STATUS[status] || { label: status || "Unknown", tone: "muted" };
}

export const isCampaignActive = (status) =>
  status === "queued" || status === "running";

export const isCampaignFinished = (status) =>
  status === "done" || status === "cancelled" || status === "failed";

export function normalizeCampaign(raw) {
  const c = raw || {};
  let filters = null;
  if (c.filters && typeof c.filters === "object") filters = c.filters;
  else if (typeof c.filters === "string") {
    try {
      filters = JSON.parse(c.filters);
    } catch (err) {
      filters = null;
    }
  }
  // The contract names `stalled` both as a status value and as a computation from
  // the heartbeat; accept either spelling so the Resume button appears in both.
  const status = c.stalled === true ? "stalled" : str(c.status) || "queued";
  return {
    id: c.id,
    practiceId: c.practice_id == null ? null : c.practice_id,
    channel: str(c.channel),
    templateId: c.template_id == null ? null : c.template_id,
    templateDefaultId: c.template_default_id == null ? null : c.template_default_id,
    templateName: str(c.template_name),
    status,
    createdBy: str(c.created_by),
    createdAt: str(c.created_at),
    startedAt: str(c.started_at),
    finishedAt: str(c.finished_at),
    heartbeatAt: str(c.heartbeat_at),
    ratePerSec: num(c.rate_per_sec) ?? RATE_DEFAULT,
    dryRun: c.dry_run === true,
    total: num(c.total) ?? 0,
    sent: num(c.sent) ?? 0,
    failed: num(c.failed) ?? 0,
    skipped: num(c.skipped) ?? 0,
    error: str(c.error),
    filters,
  };
}

export function normalizeProgress(raw, campaign) {
  const p = raw || {};
  const c = campaign || {};
  return {
    total: num(p.total) ?? c.total ?? 0,
    sent: num(p.sent) ?? c.sent ?? 0,
    failed: num(p.failed) ?? c.failed ?? 0,
    skipped: num(p.skipped) ?? c.skipped ?? 0,
    pending: num(p.pending) ?? 0,
    delivered: num(p.delivered) ?? 0,
  };
}

export function normalizeSummary(raw) {
  const s = raw || {};
  return {
    sent: num(s.sent) ?? 0,
    delivered: num(s.delivered) ?? 0,
    failed: num(s.failed) ?? 0,
    skippedOptout: num(s.skipped_optout) ?? 0,
    skippedNoBalance: num(s.skipped_no_balance) ?? 0,
    skippedCap: num(s.skipped_cap) ?? 0,
    // A second selected patient on the same mobile / email (one text per number).
    skippedDuplicate: num(s.skipped_duplicate) ?? 0,
    // Left unsent by a Cancel.
    cancelled: num(s.cancelled) ?? 0,
    unknown: num(s.unknown) ?? 0,
    replies: num(s.replies) ?? 0,
    optedOut: num(s.opted_out) ?? 0,
    paymentsCents: num(s.payments_cents) ?? 0,
    paymentsCount: num(s.payments_count) ?? 0,
    windowEnd: str(s.window_end),
  };
}

// POST …/campaigns/preview {channel, template_id, patient_ids} → the first three
// rendered messages with masked addresses. NEVER sends; the server writes nothing.
export async function previewCampaign(
  practiceId,
  { channel, templateId, patientIds },
) {
  const body = await request(
    `${base(practiceId)}/campaigns/preview`,
    post({
      channel,
      template_id: Number(templateId),
      patient_ids: (patientIds || []).slice(0, 3),
    }),
    "Could not build the preview.",
  );
  if (!Array.isArray(body.messages)) {
    throw new Error(
      "The response could not be read, so the preview may be incomplete. Try again.",
    );
  }
  return body.messages.map((m) => {
    const r = m || {};
    return {
      patientId: r.patient_id,
      subject: str(r.subject),
      body: str(r.body) || "",
      toAddressMasked: str(r.to_address_masked),
      // Only an explicit true may send; a body we cannot read never enables it.
      canSend: r.can_send === true,
      reason: str(r.reason),
    };
  });
}

// POST …/campaigns {channel, template_id, patient_ids, rate_per_sec, confirm: true}
// → 202 {campaign}. Errors carry `status`: 400 (template kind, rate, ids not in the
// practice), 409 (the daily cap, a campaign already running) and 503 (campaigns
// unavailable until the migration) all carry the server's own sentence.
//
// `dry_run` is NOT posted: the server defaults it to its own setting and refuses a
// False while that setting is on, so the UI has nothing truthful to say about it.
// `filters` is the page's current filter set, in the server's own key names, so the
// campaign row records which list the selection came from (the server keeps only
// those six keys, coerced; never a name).
export function campaignFilters(filters) {
  const f = { ...DEFAULT_FILTERS, ...(filters || {}) };
  return {
    min_balance_cents: Number(f.minBalanceCents ?? 0),
    oldest_dos_before_days: Number(f.oldestDosBeforeDays ?? 0),
    last_outreach_before_days: Number(f.lastOutreachBeforeDays ?? 0),
    has_mobile: f.hasMobile === true,
    has_email: f.hasEmail === true,
    include_opted_out: f.includeOptedOut === true,
  };
}

export async function startCampaign(
  practiceId,
  { channel, templateId, patientIds, ratePerSec, filters },
) {
  const body = await request(
    `${base(practiceId)}/campaigns`,
    post({
      channel,
      template_id: Number(templateId),
      patient_ids: patientIds,
      rate_per_sec: Number(ratePerSec),
      filters: campaignFilters(filters),
      confirm: true,
    }),
    "The campaign could not be started.",
  );
  if (!body.campaign || typeof body.campaign !== "object") {
    throw new Error(
      "The response could not be read, so the campaign may or may not have started. Check the campaigns list before starting another.",
    );
  }
  return normalizeCampaign(body.campaign);
}

// GET …/campaigns?limit=20 → newest first, counts only.
export async function fetchCampaigns(practiceId, limit = 20) {
  const qs = new URLSearchParams({ limit: String(limit) });
  const body = await request(
    `${base(practiceId)}/campaigns?${qs.toString()}`,
    {},
    "Could not load the campaigns.",
  );
  if (!Array.isArray(body.campaigns)) {
    throw new Error(
      "The response could not be read, so the campaigns may be incomplete. Try again.",
    );
  }
  return body.campaigns.map(normalizeCampaign);
}

// GET …/campaigns/<id> → {campaign, progress, summary}.
export async function fetchCampaign(practiceId, campaignId) {
  const body = await request(
    campaignBase(practiceId, campaignId),
    {},
    "Could not load the campaign.",
  );
  if (!body.campaign || typeof body.campaign !== "object") {
    throw new Error(
      "The response could not be read, so the campaign may be incomplete. Try again.",
    );
  }
  const campaign = normalizeCampaign(body.campaign);
  return {
    campaign,
    progress: normalizeProgress(body.progress, campaign),
    summary: normalizeSummary(body.summary),
  };
}

// POST …/campaigns/<id>/cancel → pending recipients are skipped; the worker stops at
// its next check. Returns the campaign when the server includes it.
export async function cancelCampaign(practiceId, campaignId) {
  const body = await request(
    `${campaignBase(practiceId, campaignId)}/cancel`,
    post({}),
    "The campaign could not be cancelled.",
  );
  return body.campaign ? normalizeCampaign(body.campaign) : null;
}

// POST …/campaigns/<id>/resume → only when stalled (or failed with pending
// recipients); leftovers become `unknown` and are never re-sent.
export async function resumeCampaign(practiceId, campaignId) {
  const body = await request(
    `${campaignBase(practiceId, campaignId)}/resume`,
    post({}),
    "The campaign could not be resumed.",
  );
  return body.campaign ? normalizeCampaign(body.campaign) : null;
}
